import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronDown, ChevronRight, Database } from "lucide-react";

export interface McpResource {
  uri: string;
  name: string;
  description?: string;
  mimeType?: string;
}

interface ResourceSelectorProps {
  resources: McpResource[] | null;
  selectedResource: string;
  onResourceSelect: (uri: string) => void;
  isLoading: boolean;
  error: Error | null;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  resourcesNotSupported?: boolean;
}

export function ResourceSelector({
  resources,
  selectedResource,
  onResourceSelect,
  isLoading,
  error,
  isExpanded,
  onToggleExpanded,
  resourcesNotSupported,
}: ResourceSelectorProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5 text-primary" />
            Resources ({resources?.length ?? 0})
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onToggleExpanded}>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0">
          <div className="space-y-2">
            {isLoading && (
              <div className="h-20 bg-muted animate-pulse rounded" />
            )}
            {error && !resourcesNotSupported && (
              <div className="text-sm text-destructive">
                Failed to load resources
              </div>
            )}
            {resourcesNotSupported && (
              <div className="text-sm text-muted-foreground">
                Resources are not supported by this MCP server.
              </div>
            )}
            {(resources || []).map((resource) => (
              <button
                key={resource.uri}
                type="button"
                className={`p-3 rounded-lg border transition-colors hover:bg-muted text-left w-full ${
                  selectedResource === resource.uri
                    ? "bg-primary/10 border-primary"
                    : ""
                }`}
                onClick={() => onResourceSelect(resource.uri)}
                aria-pressed={selectedResource === resource.uri}
              >
                <div className="font-medium">{resource.name}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {resource.description}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    {resource.mimeType ?? "unknown"}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
